/**
 * MediaAlbum/AlbumEditor.jsx
 * ════════════════════════════════════════════════════════════════
 * Валідація:
 *   - Зберегти → без валідації (чернетка)
 *   - Опублікувати (toggle is_public) → повна валідація:
 *       назва, категорія, дата події, опис ≥120 символів,
 *       мінімум 1 фото, підпис до кожного фото
 *   - Inline помилки після першої спроби публікації
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSave, faUpload, faEye, faEyeSlash,
    faTrash, faStar, faImage, faPenToSquare, faGear,
    faCircleExclamation, faPlay, faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import {
    getAlbum, updateAlbum,
    uploadAlbumFile, updateFileCaption, setFilePublic, fileToTrash,
    getYoutubeInfo, addAlbumYoutube, removeAlbumYoutube,
} from './actions';
import { toast } from 'react-toastify';
import FileViewer from '../DocumentsList/FileViewer';

// ─── Константи ────────────────────────────────────────────────────────────────

const CATEGORIES = [
    { value: '',         label: 'Без категорії' },
    { value: 'event',    label: 'Свято / захід' },
    { value: 'sport',    label: 'Спорт' },
    { value: 'creative', label: 'Творчість' },
    { value: 'room',     label: 'Приміщення' },
    { value: 'walk',     label: 'Прогулянка' },
    { value: 'other',    label: 'Інше' },
];

const PANEL_WIDTHS = {
    info:        320,
    description: 520,
};

const DESC_MIN_LEN = 120;

// ─── Допоміжні ───────────────────────────────────────────────────────────────

// Витягує чистий текст з HTML (для підрахунку символів опису)
const stripHtml = function(html) {
    if (!html) return '';
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

// Валідація альбому перед публікацією
const validateAlbum = function(fields, files) {
    var errors = {};

    if (!fields.name || !fields.name.trim()) {
        errors.name = "Вкажіть назву альбому";
    }

    if (!fields.category) {
        errors.category = "Оберіть категорію";
    }

    if (!fields.eventDate) {
        errors.eventDate = "Вкажіть дату події";
    }

    var descLen = stripHtml(fields.description).length;
    if (descLen < DESC_MIN_LEN) {
        errors.description = "Опис має бути не менше " + DESC_MIN_LEN +
            " символів (зараз " + descLen + ")";
    }

    var activeFiles = files.filter(function(f) { return !f.in_trash; });

    if (activeFiles.length === 0) {
        errors.photos = "Додайте хоча б одне фото до альбому";
    } else {
        var noCaption = activeFiles.filter(function(f) {
            return !f.caption || !f.caption.trim();
        });
        if (noCaption.length > 0) {
            errors.captions = "Додайте підпис до " +
                (noCaption.length === 1
                    ? "1 фото"
                    : noCaption.length + " фото");
        }
    }

    return errors; // порожній об'єкт = все ок
};

// ─── Компонент помилки поля ───────────────────────────────────────────────────

const FieldError = function({ message }) {
    if (!message) return null;
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 5,
            marginTop: 5, fontSize: 12, color: '#dc3545',
            lineHeight: 1.4,
        }}>
            <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ fontSize: 12, marginTop: 1, flexShrink: 0 }}
            />
            {message}
        </div>
    );
};

// ─── Лічильник символів опису ─────────────────────────────────────────────────

const DescCounter = function({ html, hasError }) {
    var len = stripHtml(html).length;
    var color = len >= DESC_MIN_LEN ? '#198754' : hasError ? '#dc3545' : '#adb5bd';
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: '#adb5bd' }}>
                Буде показано у публічній галереї
            </span>
            <span style={{ fontSize: 12, color: color, fontWeight: len >= DESC_MIN_LEN ? 400 : 500 }}>
                {len} / {DESC_MIN_LEN}
            </span>
        </div>
    );
};

// ─── Форма додавання YouTube відео ───────────────────────────────────────────

const YoutubeForm = function({ onAdd, onCancel, saving }) {
    const [url, setUrl]         = useState('');
    const [caption, setCaption] = useState('');
    const [meta, setMeta]       = useState(null);   // { title, thumbnail_url, duration }
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState('');

    var isValid = /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/.test(url.trim());

    var handleCheck = async function() {
        if (!isValid) { setError("Введіть коректне YouTube посилання"); return; }
        setError(''); setMeta(null); setLoading(true);
        try {
            var data = await getYoutubeInfo(url.trim());

            // Перевіряємо що отримали title — ознака успіху
            if (!data || !data.title) {
                setError("Відео не знайдено або недоступне. Перевірте що відео публічне.");
                return;
            }

            setMeta(data);
            if (!caption && data.title) setCaption(data.title);
        } catch (e) {
            console.error('YouTube check error:', e);
            // Якщо помилка HTTP — скоріш за все роут не зареєстрований або відео приватне
            var msg = e && e.message && e.message.includes('404')
                ? "Ендпоінт youtube_oembed не знайдено. Перевірте реєстрацію роутів на бекенді."
                : "Не вдалося отримати дані відео. Відео може бути приватним.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    var handleAdd = async function() {
        if (!meta) return;
        await onAdd(url.trim(), { caption, title: meta.title, thumbnail_url: meta.thumbnail && meta.thumbnail.url });
        setUrl(''); setCaption(''); setMeta(null);
    };

    return (
        <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faYoutube} style={{ color: '#ff0000', fontSize: 16 }} />
                Додати відео з YouTube
            </div>

            {/* URL */}
            <div style={{ display: 'flex', gap: 8, marginBottom: error ? 4 : 10 }}>
                <input
                    type="text"
                    value={url}
                    onChange={function(e) { setUrl(e.target.value); setMeta(null); setError(''); }}
                    onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); handleCheck(); } }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    style={{
                        flex: 1, padding: '6px 10px', fontSize: 13,
                        border: '1px solid ' + (error ? '#dc3545' : '#ced4da'),
                        borderRadius: 6, outline: 'none',
                    }}
                />
                <button
                    type="button"
                    onClick={handleCheck}
                    disabled={!url || loading}
                    style={{
                        padding: '6px 12px', borderRadius: 6, border: '1px solid #ced4da',
                        background: '#fff', cursor: url && !loading ? 'pointer' : 'not-allowed',
                        fontSize: 13, display: 'flex', alignItems: 'center', gap: 5,
                        opacity: !url || loading ? 0.6 : 1,
                    }}
                >
                    {loading
                        ? <Spinner size="sm" animation="border" />
                        : <><FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: 12 }} />Перевірити</>}
                </button>
            </div>
            {error && <div style={{ fontSize: 12, color: '#dc3545', marginBottom: 8 }}>{error}</div>}

            {/* Превью */}
            {meta && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #dee2e6', marginBottom: 10 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <img
                            src={meta.thumbnail && meta.thumbnail.url}
                            alt={meta.title}
                            style={{ width: 80, height: 56, objectFit: 'cover', borderRadius: 5, display: 'block' }}
                        />
                        {meta.duration && meta.duration.formatted && (
                            <div style={{ position: 'absolute', bottom: 3, right: 3, background: 'rgba(0,0,0,0.75)', borderRadius: 3, padding: '1px 4px', fontSize: 10, color: '#fff', fontWeight: 500 }}>
                                {meta.duration.formatted}
                            </div>
                        )}
                        <div style={{ position: 'absolute', top: 3, left: 3, background: '#ff0000', borderRadius: 3, padding: '1px 4px' }}>
                            <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 9, color: '#fff' }} />
                        </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{meta.title}</div>
                        {meta.channelTitle && <div style={{ fontSize: 11, color: '#6c757d' }}>{meta.channelTitle}</div>}
                    </div>
                </div>
            )}

            {/* Підпис */}
            {meta && (
                <div style={{ marginBottom: 10 }}>
                    <input
                        type="text"
                        value={caption}
                        onChange={function(e) { setCaption(e.target.value); }}
                        placeholder="Підпис до відео *"
                        style={{
                            width: '100%', padding: '6px 10px', fontSize: 13,
                            border: '1px solid #ced4da', borderRadius: 6, outline: 'none',
                        }}
                    />
                    {!caption.trim() && (
                        <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 3 }}>Підпис обов'язковий</div>
                    )}
                </div>
            )}

            {/* Кнопки */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={onCancel}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ced4da', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                    Скасувати
                </button>
                {meta && (
                    <button type="button" onClick={handleAdd}
                        disabled={!caption.trim() || saving}
                        style={{
                            padding: '6px 14px', borderRadius: 6, border: 'none',
                            background: !caption.trim() || saving ? '#ced4da' : '#dc3545',
                            color: '#fff', cursor: !caption.trim() || saving ? 'not-allowed' : 'pointer',
                            fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faYoutube} />Додати відео</>}
                    </button>
                )}
            </div>
        </div>
    );
};

// ─── Картка YouTube відео ─────────────────────────────────────────────────────

const VideoCard = function({ file, albumId, onRemove, showCaptionError }) {
    const [caption, setCaption] = useState(file.caption || '');
    const [isPublic, setIsPublic] = useState(!!file.is_public);
    const [saving, setSaving]   = useState(false);
    const [hovered, setHovered] = useState(false);
    const savedCaption = useRef(file.caption || '');
    const [touched, setTouched] = useState(false);

    var captionEmpty = !caption || !caption.trim();
    var showError    = captionEmpty && (showCaptionError || touched);

    var handleCaptionBlur = async function() {
        setTouched(true);
        if (caption === savedCaption.current) return;
        setSaving(true);
        try {
            await updateFileCaption(albumId, file.id, caption);
            savedCaption.current = caption;
            file.caption = caption;
        } catch (e) {
            toast.error('Помилка збереження підпису');
        } finally {
            setSaving(false);
        }
    };

    var handleTogglePublic = async function() {
        var newVal = !isPublic;
        setIsPublic(newVal);
        try {
            await setFilePublic(albumId, file.id, newVal);
            file.is_public = newVal;
        } catch (e) {
            setIsPublic(!newVal);
            toast.error('Помилка зміни видимості');
        }
    };

    return (
        <div
            style={{
                border: showError ? '2px solid #dc3545' : hovered ? '1px solid #86b7fe' : '1px solid #dee2e6',
                borderRadius: 10, overflow: 'hidden', background: '#fff', position: 'relative',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                boxShadow: showError ? '0 0 0 3px rgba(220,53,69,0.12)' : hovered ? '0 2px 10px rgba(13,110,253,0.12)' : 'none',
            }}
            onMouseEnter={function() { setHovered(true); }}
            onMouseLeave={function() { setHovered(false); }}
        >
            {/* Thumbnail */}
            <div
                style={{ width: '100%', aspectRatio: '16/9', background: '#111', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                onClick={function() { window.open(file.youtube_url, '_blank', 'noopener'); }}
            >
                {file.thumbnail_url ? (
                    <img
                        src={file.thumbnail_url}
                        alt={file.caption || file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85, transition: 'opacity 0.15s' }}
                        onMouseEnter={function(e) { e.target.style.opacity = '1'; }}
                        onMouseLeave={function(e) { e.target.style.opacity = '0.85'; }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 40, color: '#ff0000' }} />
                    </div>
                )}

                {/* Play overlay */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faPlay} style={{ fontSize: 16, color: '#fff', marginLeft: 3 }} />
                    </div>
                </div>

                {/* YouTube badge */}
                <div style={{ position: 'absolute', top: 6, left: 6, background: '#ff0000', borderRadius: 4, padding: '2px 7px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 11, color: '#fff' }} />
                    <span style={{ fontSize: 10, color: '#fff', fontWeight: 500 }}>YouTube</span>
                </div>

                {/* Тривалість */}
                {file.duration_fmt && (
                    <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.75)', borderRadius: 4, padding: '1px 6px', fontSize: 11, color: '#fff', fontWeight: 500 }}>
                        {file.duration_fmt}
                    </div>
                )}

                {/* Бейдж "приховано" */}
                {!isPublic && (
                    <div style={{
                        position: 'absolute', top: 6, right: 6,
                        background: 'rgba(0,0,0,0.55)', borderRadius: 4,
                        padding: '1px 6px', fontSize: 10, color: '#fff',
                        display: 'flex', alignItems: 'center', gap: 3,
                    }}>
                        <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: 10 }} />
                        приховано
                    </div>
                )}

                {/* Hover кнопки */}
                {hovered && (
                    <div
                        style={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', gap: 4 }}
                        onClick={function(e) { e.stopPropagation(); }}
                    >
                        <IconBtn
                            icon={isPublic ? faEyeSlash : faEye}
                            title={isPublic ? 'Приховати' : 'Показати публічно'}
                            onClick={handleTogglePublic}
                            color={isPublic ? '#adb5bd' : '#7ed47e'}
                        />
                        <IconBtn
                            icon={faTrash}
                            title="Видалити відео"
                            onClick={function() { onRemove(file.id); }}
                            color="#ff8080"
                        />
                    </div>
                )}
            </div>

            {/* Підпис */}
            <div style={{ padding: '7px 10px 8px' }}>
                <input
                    type="text"
                    value={caption}
                    onChange={function(e) { setCaption(e.target.value); file.caption = e.target.value; }}
                    onBlur={handleCaptionBlur}
                    placeholder={showError ? "⚠ Підпис обов'язковий" : "Підпис до відео..."}
                    style={{
                        width: '100%', border: 'none', outline: 'none',
                        fontSize: 13, color: showError ? '#dc3545' : '#495057',
                        background: 'transparent',
                        borderBottom: '1px dashed ' + (showError ? '#dc3545' : 'transparent'),
                        padding: '2px 0',
                    }}
                    onFocus={function(e) { e.target.style.borderBottomColor = showError ? '#dc3545' : '#86b7fe'; }}
                    onBlurCapture={function(e) { e.target.style.borderBottomColor = showError ? '#dc3545' : 'transparent'; }}
                />
                {saving && <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 2 }}>зберігається...</div>}
            </div>
        </div>
    );
};

// ─── Rich Text Editor ─────────────────────────────────────────────────────────

const RichEditor = ({ value, onChange, placeholder, hasError }) => {
    const ref = useRef(null);
    const isInitialized = useRef(false);

    useEffect(function() {
        if (ref.current && !isInitialized.current) {
            ref.current.innerHTML = value || '';
            isInitialized.current = true;
        }
    }, []);

    const exec = function(cmd) {
        document.execCommand(cmd, false, null);
        ref.current && ref.current.focus();
    };

    const toolbarBtns = [
        { cmd: 'bold',                label: 'Ж',   style: { fontWeight: 'bold' } },
        { cmd: 'italic',              label: 'К',   style: { fontStyle: 'italic' } },
        { cmd: 'underline',           label: 'П',   style: { textDecoration: 'underline' } },
        { cmd: 'insertUnorderedList', label: '• —', style: {} },
        { cmd: 'insertOrderedList',   label: '1.',  style: {} },
    ];

    var borderColor = hasError ? '#dc3545' : '#ced4da';

    return (
        <div style={{ border: '1px solid ' + borderColor, borderRadius: 8, overflow: 'hidden', transition: 'border-color 0.15s' }}>
            <div style={{ display: 'flex', gap: 3, padding: '6px 8px', borderBottom: '1px solid ' + borderColor, background: '#f8f9fa', flexWrap: 'wrap' }}>
                {toolbarBtns.map(function(btn) {
                    return (
                        <button
                            key={btn.cmd}
                            type="button"
                            onMouseDown={function(e) { e.preventDefault(); exec(btn.cmd); }}
                            style={Object.assign({
                                padding: '2px 8px', height: 28,
                                border: '1px solid #ced4da', borderRadius: 5,
                                background: '#fff', cursor: 'pointer', fontSize: 13,
                                minWidth: 32,
                            }, btn.style)}
                        >
                            {btn.label}
                        </button>
                    );
                })}
            </div>
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={function() { onChange(ref.current.innerHTML); }}
                style={{
                    minHeight: 240,
                    maxHeight: 420,
                    overflowY: 'auto',
                    padding: '12px 14px',
                    fontSize: 14,
                    lineHeight: 1.7,
                    outline: 'none',
                    color: '#212529',
                }}
            />
            {!value && (
                <div style={{
                    position: 'relative',
                    marginTop: -8,
                    padding: '0 14px 10px',
                    fontSize: 13,
                    color: '#adb5bd',
                    pointerEvents: 'none',
                }}>
                    {placeholder}
                </div>
            )}
        </div>
    );
};

// ─── Картка файлу ─────────────────────────────────────────────────────────────

const FileCard = ({ file, albumId, onTrash, onSetCover, isCover, onView, showCaptionError }) => {
    const [caption, setCaption]   = useState(file.caption || '');
    const [isPublic, setIsPublic] = useState(!!file.is_public);
    const [saving, setSaving]     = useState(false);
    const [hovered, setHovered]   = useState(false);
    const [touched, setTouched]   = useState(false);
    const savedCaption = useRef(file.caption || '');

    // Показувати помилку підпису якщо:
    // - батьківський компонент сигналізує (після спроби публікації) АБО поле було touched
    var captionEmpty   = !caption || !caption.trim();
    var showError      = captionEmpty && (showCaptionError || touched);

    const handleCaptionBlur = async function() {
        setTouched(true);
        if (caption === savedCaption.current) return;
        setSaving(true);
        try {
            await updateFileCaption(albumId, file.id, caption);
            savedCaption.current = caption;
            // Оновлюємо file.caption щоб валідація батька знала актуальний стан
            file.caption = caption;
        } catch (e) {
            toast.error('Помилка збереження підпису');
        } finally {
            setSaving(false);
        }
    };

    const handleTogglePublic = async function() {
        const newVal = !isPublic;
        setIsPublic(newVal);
        try {
            await setFilePublic(albumId, file.id, newVal);
        } catch (e) {
            setIsPublic(!newVal);
            toast.error('Помилка зміни видимості');
        }
    };

    const isImage = file.mtype && file.mtype.match(/image\//i);

    return (
        <div
            style={{
                border: showError
                    ? '2px solid #dc3545'
                    : hovered ? '1px solid #86b7fe' : '1px solid #dee2e6',
                borderRadius: 10,
                overflow: 'hidden',
                background: '#fff',
                position: 'relative',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                boxShadow: showError
                    ? '0 0 0 3px rgba(220,53,69,0.12)'
                    : hovered ? '0 2px 10px rgba(13,110,253,0.12)' : 'none',
            }}
            onMouseEnter={function() { setHovered(true); }}
            onMouseLeave={function() { setHovered(false); }}
        >
            {/* Фото */}
            <div
                style={{ width: '100%', aspectRatio: '4/3', background: '#f0f0f0', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}
                onClick={function() { onView(file); }}
            >
                {isImage ? (
                    <img
                        src={constr_uri('docfile?doc_id=' + albumId + '&file_id=' + file.id)}
                        alt={file.caption || file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s' }}
                        loading="lazy"
                        onMouseEnter={function(e) { e.target.style.transform = 'scale(1.03)'; }}
                        onMouseLeave={function(e) { e.target.style.transform = 'scale(1)'; }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>
                        📄
                    </div>
                )}

                {/* Бейджі */}
                <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-start' }}>
                    {isCover && (
                        <span style={{ background: '#ffc107', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600, color: '#000' }}>
                            обкладинка
                        </span>
                    )}
                    {!isPublic && (
                        <span style={{ background: 'rgba(0,0,0,0.55)', borderRadius: 4, padding: '1px 7px', fontSize: 11, color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: 10 }} />
                            приховано
                        </span>
                    )}
                </div>

                {/* Hover кнопки */}
                {hovered && (
                    <div
                        style={{ position: 'absolute', bottom: 6, right: 6, display: 'flex', gap: 4 }}
                        onClick={function(e) { e.stopPropagation(); }}
                    >
                        {isImage && !isCover && (
                            <IconBtn icon={faStar} title="Зробити обкладинкою" onClick={function() { onSetCover(file.id); }} color="#ffc107" />
                        )}
                        <IconBtn
                            icon={isPublic ? faEyeSlash : faEye}
                            title={isPublic ? 'Приховати' : 'Показати публічно'}
                            onClick={handleTogglePublic}
                            color={isPublic ? '#adb5bd' : '#7ed47e'}
                        />
                        <IconBtn icon={faTrash} title="До кошика" onClick={function() { onTrash(file.id); }} color="#ff8080" />
                    </div>
                )}
            </div>

            {/* Підпис */}
            <div style={{ padding: '7px 10px 8px' }}>
                <input
                    type="text"
                    value={caption}
                    onChange={function(e) { setCaption(e.target.value); file.caption = e.target.value; }}
                    onBlur={handleCaptionBlur}
                    placeholder={showError ? "⚠ Підпис обов'язковий" : "Підпис до фото..."}
                    style={{
                        width: '100%', border: 'none', outline: 'none',
                        fontSize: 13,
                        color: showError ? '#dc3545' : '#495057',
                        background: 'transparent',
                        borderBottom: '1px dashed ' + (showError ? '#dc3545' : 'transparent'),
                        padding: '2px 0',
                    }}
                    onFocus={function(e) { e.target.style.borderBottomColor = showError ? '#dc3545' : '#86b7fe'; }}
                    onBlurCapture={function(e) { e.target.style.borderBottomColor = showError ? '#dc3545' : 'transparent'; }}
                />
                {saving && (
                    <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 2 }}>зберігається...</div>
                )}
            </div>
        </div>
    );
};

const IconBtn = function({ icon, title, onClick, color }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            style={{
                width: 28, height: 28, borderRadius: 6,
                background: 'rgba(0,0,0,0.6)', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: color || '#fff',
                transition: 'background 0.15s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(0,0,0,0.8)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(0,0,0,0.6)'; }}
        >
            <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
        </button>
    );
};

// ─── Підсумок помилок (банер) ─────────────────────────────────────────────────

const ValidationSummary = function({ errors, onGoTo }) {
    var list = Object.keys(errors);
    if (list.length === 0) return null;

    return (
        <div style={{
            background: '#fff5f5',
            border: '1px solid #f5c2c7',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
        }}>
            <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ fontSize: 18, color: '#dc3545', marginTop: 1, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#842029', marginBottom: 6 }}>
                    Для публікації виправте {list.length === 1 ? '1 помилку' : list.length + ' помилки'}:
                </div>
                <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                    {errors.name && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>
                            <button type="button" onClick={function() { onGoTo('info', 'name'); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#842029', textDecoration: 'underline', fontSize: 13 }}>
                                {errors.name}
                            </button>
                        </li>
                    )}
                    {errors.category && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>
                            <button type="button" onClick={function() { onGoTo('info', 'category'); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#842029', textDecoration: 'underline', fontSize: 13 }}>
                                {errors.category}
                            </button>
                        </li>
                    )}
                    {errors.eventDate && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>
                            <button type="button" onClick={function() { onGoTo('info', 'eventDate'); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#842029', textDecoration: 'underline', fontSize: 13 }}>
                                {errors.eventDate}
                            </button>
                        </li>
                    )}
                    {errors.description && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>
                            <button type="button" onClick={function() { onGoTo('description'); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#842029', textDecoration: 'underline', fontSize: 13 }}>
                                {errors.description}
                            </button>
                        </li>
                    )}
                    {errors.photos && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>{errors.photos}</li>
                    )}
                    {errors.captions && (
                        <li style={{ fontSize: 13, color: '#842029', marginBottom: 3 }}>{errors.captions}</li>
                    )}
                </ul>
            </div>
        </div>
    );
};

// ─── Ліва панель ─────────────────────────────────────────────────────────────

const LeftPanel = ({
    activeTab, onTabChange,
    name, setName,
    description, setDesc,
    category, setCategory,
    groupIds, toggleGroup,
    eventDate, setEventDate,
    isPublic, onTogglePublic,
    groups, errors,
    nameRef, categoryRef, eventDateRef,
}) => {
    const panelWidth = PANEL_WIDTHS[activeTab] || PANEL_WIDTHS.info;

    return (
        <div style={{ width: panelWidth, flexShrink: 0, transition: 'width 0.25s ease' }}>
            {/* Таби */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', marginBottom: 16 }}>
                <button type="button" onClick={function() { onTabChange('info'); }}
                    style={{
                        padding: '7px 14px', fontSize: 13, border: 'none', background: 'transparent', cursor: 'pointer',
                        borderBottom: activeTab === 'info' ? '2px solid #0d6efd' : '2px solid transparent',
                        color: activeTab === 'info' ? '#0d6efd' : '#6c757d',
                        fontWeight: activeTab === 'info' ? 500 : 400,
                        display: 'flex', alignItems: 'center', gap: 5,
                    }}
                >
                    <FontAwesomeIcon icon={faGear} style={{ fontSize: 12 }} />
                    Налаштування
                    {/* Червона крапка якщо є помилки в цьому табі */}
                    {(errors.name || errors.category || errors.eventDate) && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }} />
                    )}
                </button>
                <button type="button" onClick={function() { onTabChange('description'); }}
                    style={{
                        padding: '7px 14px', fontSize: 13, border: 'none', background: 'transparent', cursor: 'pointer',
                        borderBottom: activeTab === 'description' ? '2px solid #0d6efd' : '2px solid transparent',
                        color: activeTab === 'description' ? '#0d6efd' : '#6c757d',
                        fontWeight: activeTab === 'description' ? 500 : 400,
                        display: 'flex', alignItems: 'center', gap: 5,
                    }}
                >
                    <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: 12 }} />
                    Опис
                    {errors.description && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }} />
                    )}
                    {!errors.description && description && description !== '<br>' && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#0d6efd', display: 'inline-block' }} />
                    )}
                </button>
            </div>

            {/* Таб: Налаштування */}
            {activeTab === 'info' && (
                <div style={{ border: '1px solid #dee2e6', borderRadius: 12, padding: 16, background: '#fff' }}>

                    {/* Назва */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, color: '#6c757d', display: 'block', marginBottom: 5, fontWeight: 500 }}>
                            Назва альбому <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <Form.Control
                            ref={nameRef}
                            value={name}
                            onChange={function(e) { setName(e.target.value); }}
                            placeholder="Новорічний ранок 2025"
                            style={{
                                fontSize: 14,
                                borderColor: errors.name ? '#dc3545' : undefined,
                                boxShadow: errors.name ? '0 0 0 3px rgba(220,53,69,0.12)' : undefined,
                            }}
                        />
                        <FieldError message={errors.name} />
                    </div>

                    {/* Категорія */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, color: '#6c757d', display: 'block', marginBottom: 5, fontWeight: 500 }}>
                            Категорія <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <Form.Select
                            ref={categoryRef}
                            value={category}
                            onChange={function(e) { setCategory(e.target.value); }}
                            style={{
                                fontSize: 14,
                                borderColor: errors.category ? '#dc3545' : undefined,
                                boxShadow: errors.category ? '0 0 0 3px rgba(220,53,69,0.12)' : undefined,
                            }}
                        >
                            {CATEGORIES.map(function(c) {
                                return <option key={c.value} value={c.value}>{c.label}</option>;
                            })}
                        </Form.Select>
                        <FieldError message={errors.category} />
                    </div>

                    {/* Дата події */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, color: '#6c757d', display: 'block', marginBottom: 5, fontWeight: 500 }}>
                            Дата події <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <Form.Control
                            ref={eventDateRef}
                            type="date"
                            value={eventDate}
                            onChange={function(e) { setEventDate(e.target.value); }}
                            style={{
                                fontSize: 14,
                                borderColor: errors.eventDate ? '#dc3545' : undefined,
                                boxShadow: errors.eventDate ? '0 0 0 3px rgba(220,53,69,0.12)' : undefined,
                            }}
                        />
                        <FieldError message={errors.eventDate} />
                    </div>

                    {/* Групи */}
                    {groups.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, color: '#6c757d', display: 'block', marginBottom: 8, fontWeight: 500 }}>
                                Групи
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                <button type="button" onClick={function() { toggleGroup(null); }}
                                    style={{ padding: '4px 12px', borderRadius: 20, fontSize: 13, border: '1px solid ' + (groupIds.length === 0 ? '#86b7fe' : '#ced4da'), background: groupIds.length === 0 ? '#e6f1fb' : '#fff', color: groupIds.length === 0 ? '#0c447c' : '#6c757d', cursor: 'pointer' }}>
                                    Всі
                                </button>
                                {groups.map(function(g) {
                                    var sel = groupIds.indexOf(g._id) !== -1;
                                    return (
                                        <button key={g._id} type="button" onClick={function() { toggleGroup(g._id); }}
                                            style={{ padding: '4px 12px', borderRadius: 20, fontSize: 13, border: '1px solid ' + (sel ? '#86b7fe' : '#ced4da'), background: sel ? '#e6f1fb' : '#fff', color: sel ? '#0c447c' : '#6c757d', cursor: 'pointer' }}>
                                            {g.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Публічність */}
                    <div style={{ paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 500 }}>Публічний альбом</div>
                                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                                    {isPublic ? 'Видно у галереї садочку' : 'Прихований від відвідувачів'}
                                </div>
                            </div>
                            <button type="button" onClick={onTogglePublic}
                                style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: isPublic ? '#198754' : '#ced4da', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                                <span style={{ position: 'absolute', top: 3, left: isPublic ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </button>
                        </div>
                        {isPublic && (
                            <div style={{ fontSize: 12, color: '#198754', marginTop: 8, background: '#eaf3de', padding: '4px 8px', borderRadius: 6 }}>
                                Публічність кожного фото керується кнопкою ока на картці
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Таб: Опис */}
            {activeTab === 'description' && (
                <div style={{ border: '1px solid ' + (errors.description ? '#dc3545' : '#dee2e6'), borderRadius: 12, padding: 16, background: '#fff', transition: 'border-color 0.15s' }}>
                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
                        Опис буде показано у публічній галереї поряд з фото.
                        Підтримується форматування. <span style={{ color: '#dc3545' }}>*</span>
                    </div>
                    <RichEditor
                        value={description}
                        onChange={function(val) { setDesc(val); }}
                        placeholder="Розкажіть про цю подію: коли відбулась, що робили діти, хто брав участь..."
                        hasError={!!errors.description}
                    />
                    <DescCounter html={description} hasError={!!errors.description} />
                    <FieldError message={errors.description} />
                </div>
            )}
        </div>
    );
};

// ─── Основний компонент ───────────────────────────────────────────────────────

const AlbumEditor = ({ albumId, kgId, groups, onBack }) => {
    const [album, setAlbum]         = useState(null);
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isDirty, setIsDirty]     = useState(false);
    const [leftTab, setLeftTab]     = useState('info');

    // Форма
    const [name, setName]           = useState('');
    const [description, setDesc]    = useState('');
    const [category, setCategory]   = useState('');
    const [groupIds, setGroupIds]   = useState([]);
    const [eventDate, setEventDate] = useState('');
    const [isPublic, setIsPublic]   = useState(false);
    const [coverFileId, setCoverFileId] = useState(null);
    const [files, setFiles]         = useState([]);
    const [showYoutubeForm, setShowYoutubeForm] = useState(false);
    const [savingYoutube, setSavingYoutube]     = useState(false);

    // Валідація
    const [validationErrors, setValidationErrors] = useState({});
    const [publishAttempted, setPublishAttempted] = useState(false);

    // Refs для скролу до полів з помилками
    const nameRef      = useRef(null);
    const categoryRef  = useRef(null);
    const eventDateRef = useRef(null);

    const fileInputRef = useRef(null);
    const [viewer, setViewer] = useState(null);

    useEffect(function() { loadAlbum(); }, [albumId]);

    const loadAlbum = async function() {
        setLoading(true);
        try {
            const data = await getAlbum(albumId);
            setAlbum(data);
            setName(data.name || '');
            setDesc(data.description || '');
            setCategory(data.category || '');
            setGroupIds(data.group_ids || []);
            setEventDate(data.event_date ? data.event_date.substring(0, 10) : '');
            setIsPublic(!!data.is_public);
            setCoverFileId(data.cover_file_id || null);
            setFiles(data.files || []);
        } catch (e) {
            toast.error('Помилка завантаження альбому');
        } finally {
            setLoading(false);
        }
    };

    // ── Перевалідація при зміні полів (якщо вже намагались публікувати) ────────

    useEffect(function() {
        if (!publishAttempted) return;
        var errs = validateAlbum(
            { name, description, category, eventDate },
            files
        );
        setValidationErrors(errs);
    }, [name, description, category, eventDate, files, publishAttempted]);

    // ── Зберегти (без валідації — чернетка) ─────────────────────────────────────

    const handleSave = async function() {
        setSaving(true);
        try {
            await updateAlbum(albumId, {
                name, description, category,
                group_ids:     groupIds,
                event_date:    eventDate || null,
                is_public:     isPublic,
                cover_file_id: coverFileId,
            });
            setIsDirty(false);
            toast.success('Збережено як чернетку');
        } catch (e) {
            toast.error('Помилка збереження');
        } finally {
            setSaving(false);
        }
    };

    // ── Публікувати (з валідацією) ───────────────────────────────────────────────

    const handleTogglePublic = async function() {
        var newVal = !isPublic;

        // При вимкненні публікації — без валідації
        if (!newVal) {
            setIsPublic(false);
            setIsDirty(true);
            return;
        }

        // При вмиканні — валідуємо
        setPublishAttempted(true);
        var errs = validateAlbum(
            { name, description, category, eventDate },
            files
        );
        setValidationErrors(errs);

        if (Object.keys(errs).length > 0) {
            toast.error('Виправте помилки перед публікацією');

            // Автоматично перемикаємо на таб де є перша помилка
            if (errs.name || errs.category || errs.eventDate) {
                setLeftTab('info');
                // Скрол до першого поля з помилкою
                setTimeout(function() {
                    if (errs.name && nameRef.current)           nameRef.current.focus();
                    else if (errs.category && categoryRef.current)   categoryRef.current.focus();
                    else if (errs.eventDate && eventDateRef.current) eventDateRef.current.focus();
                }, 100);
            } else if (errs.description) {
                setLeftTab('description');
            }
            return;
        }

        // Валідація пройшла — зберігаємо і публікуємо
        setSaving(true);
        try {
            await updateAlbum(albumId, {
                name, description, category,
                group_ids:     groupIds,
                event_date:    eventDate || null,
                is_public:     true,
                cover_file_id: coverFileId,
            });
            setIsPublic(true);
            setIsDirty(false);
            setValidationErrors({});
            toast.success('Альбом опубліковано');
        } catch (e) {
            toast.error('Помилка публікації');
        } finally {
            setSaving(false);
        }
    };

    // ── Навігація з банеру помилок ───────────────────────────────────────────────

    const handleGoTo = function(tab, field) {
        setLeftTab(tab);
        setTimeout(function() {
            if (field === 'name' && nameRef.current)           nameRef.current.focus();
            if (field === 'category' && categoryRef.current)   categoryRef.current.focus();
            if (field === 'eventDate' && eventDateRef.current) eventDateRef.current.focus();
        }, 150);
    };

    // ── Групи ───────────────────────────────────────────────────────────────────

    const toggleGroup = function(id) {
        if (id === null) {
            setGroupIds([]);
        } else {
            setGroupIds(function(prev) {
                if (prev.indexOf(id) !== -1) return prev.filter(function(g) { return g !== id; });
                return prev.concat([id]);
            });
        }
        setIsDirty(true);
    };

    // ── Завантаження фото ────────────────────────────────────────────────────────

    const handleFilesSelected = async function(e) {
        const selected = Array.from(e.target.files);
        if (!selected.length) return;
        e.target.value = '';
        setUploading(true);
        var failed = 0;

        for (var i = 0; i < selected.length; i++) {
            try {
                var response = await uploadAlbumFile(albumId, selected[i], { caption: '' });
                if (!response.ok) failed++;
            } catch (err) {
                failed++;
            }
        }

        if (failed > 0) toast.warning(failed + ' файл(ів) не завантажено');
        await loadAlbum();
        setUploading(false);
        if (selected.length - failed > 0) {
            toast.success('Завантажено ' + (selected.length - failed) + ' фото');
        }
    };

    const handleSetCover = function(fileId) {
        setCoverFileId(fileId);
        setIsDirty(true);
    };

    const handleTrash = async function(fileId) {
        try {
            await fileToTrash(albumId, fileId);
            setFiles(function(prev) { return prev.filter(function(f) { return f.id !== fileId; }); });
        } catch (e) {
            toast.error('Помилка');
        }
    };

    const handleAddYoutube = async function(url, meta) {
        setSavingYoutube(true);
        try {
            await addAlbumYoutube(albumId, url, meta);
            toast.success('Відео додано');
            setShowYoutubeForm(false);
            await loadAlbum();
        } catch (e) {
            toast.error('Помилка додавання відео');
        } finally {
            setSavingYoutube(false);
        }
    };

    const handleRemoveYoutube = async function(fileId) {
        if (!window.confirm('Видалити відео з альбому?')) return;
        try {
            await removeAlbumYoutube(albumId, fileId);
            setFiles(function(prev) { return prev.filter(function(f) { return f.id !== fileId; }); });
        } catch (e) {
            toast.error('Помилка видалення');
        }
    };

    const handleView = function(file) {
        if (file.source === 'youtube') {
            window.open(file.youtube_url, '_blank', 'noopener');
            return;
        }
        setViewer({ id: file.id, name: file.caption || file.name || 'фото', mtype: file.mtype });
    };

    // ── Рендер ──────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    var activeFiles      = files.filter(function(f) { return !f.in_trash; });
    var photoFiles       = activeFiles.filter(function(f) { return f.source !== 'youtube'; });
    var videoFiles       = activeFiles.filter(function(f) { return f.source === 'youtube'; });
    var hasErrors        = Object.keys(validationErrors).length > 0;
    var showCaptionError = publishAttempted && !!validationErrors.captions;

    return (
        <div>
            {/* Шапка */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <button type="button" onClick={onBack}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 18 }} />
                </button>
                <div style={{ flex: 1, fontSize: 20, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name || <span style={{ color: '#adb5bd' }}>Без назви</span>}
                </div>
                {isDirty && (
                    <Button variant="outline-secondary" onClick={handleSave} disabled={saving}>
                        {saving
                            ? <Spinner size="sm" animation="border" />
                            : <><FontAwesomeIcon icon={faSave} className="me-2" />Зберегти чернетку</>}
                    </Button>
                )}
                <Button
                    variant="outline-danger"
                    onClick={function() { setShowYoutubeForm(function(v) { return !v; }); }}
                >
                    <FontAwesomeIcon icon={faYoutube} className="me-2" />
                    Додати відео
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                    disabled={uploading}
                >
                    {uploading
                        ? <Spinner size="sm" animation="border" />
                        : <><FontAwesomeIcon icon={faUpload} className="me-2" />Додати фото</>}
                </Button>
                <input ref={fileInputRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={handleFilesSelected} />
            </div>

            {/* Банер помилок (тільки після спроби публікації) */}
            {publishAttempted && hasErrors && (
                <ValidationSummary errors={validationErrors} onGoTo={handleGoTo} />
            )}

            {/* Двоколонковий layout */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

                <LeftPanel
                    activeTab={leftTab}
                    onTabChange={setLeftTab}
                    name={name}
                    setName={function(v) { setName(v); setIsDirty(true); }}
                    description={description}
                    setDesc={function(v) { setDesc(v); setIsDirty(true); }}
                    category={category}
                    setCategory={function(v) { setCategory(v); setIsDirty(true); }}
                    groupIds={groupIds}
                    toggleGroup={toggleGroup}
                    eventDate={eventDate}
                    setEventDate={function(v) { setEventDate(v); setIsDirty(true); }}
                    isPublic={isPublic}
                    onTogglePublic={handleTogglePublic}
                    groups={groups || []}
                    errors={validationErrors}
                    nameRef={nameRef}
                    categoryRef={categoryRef}
                    eventDateRef={eventDateRef}
                />

                {/* Права панель — фото */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* YouTube форма */}
                {showYoutubeForm && (
                    <YoutubeForm
                        onAdd={handleAddYoutube}
                        onCancel={function() { setShowYoutubeForm(false); }}
                        saving={savingYoutube}
                    />
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 14, color: '#6c757d', display: 'flex', alignItems: 'center', gap: 10 }}>
                            {photoFiles.length > 0 && (
                                <span>{photoFiles.length} фото</span>
                            )}
                            {videoFiles.length > 0 && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FontAwesomeIcon icon={faYoutube} style={{ color: '#ff0000', fontSize: 13 }} />
                                    {videoFiles.length} відео
                                </span>
                            )}
                            {activeFiles.length === 0 && <span>Альбом порожній</span>}
                            {/* Помилка фото */}
                            {publishAttempted && validationErrors.photos && (
                                <span style={{ fontSize: 13, color: '#dc3545', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: 13 }} />
                                    {validationErrors.photos}
                                </span>
                            )}
                            {publishAttempted && validationErrors.captions && !validationErrors.photos && (
                                <span style={{ fontSize: 13, color: '#dc3545', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: 13 }} />
                                    {validationErrors.captions}
                                </span>
                            )}
                        </div>
                        {activeFiles.length > 0 && (
                            <div style={{ fontSize: 12, color: '#adb5bd' }}>
                                ⭐ обкладинка · 👁 видимість · підпис — обов'язковий
                            </div>
                        )}
                    </div>

                    {activeFiles.length === 0 ? (
                        <div style={{
                            border: '1.5px dashed ' + (publishAttempted && validationErrors.photos ? '#dc3545' : '#ced4da'),
                            borderRadius: 12, padding: '60px 20px', textAlign: 'center',
                            color: '#adb5bd', background: '#fafafa',
                        }}>
                            <FontAwesomeIcon icon={faImage} style={{ fontSize: 40, marginBottom: 14, display: 'block', margin: '0 auto 14px', opacity: 0.4 }} />
                            <div style={{ fontSize: 16, marginBottom: 8 }}>Додайте медіа до альбому</div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button type="button"
                                    onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                                    style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #ced4da', background: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FontAwesomeIcon icon={faUpload} />Завантажити фото
                                </button>
                                <button type="button"
                                    onClick={function() { setShowYoutubeForm(true); }}
                                    style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #ced4da', background: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: '#dc3545' }}>
                                    <FontAwesomeIcon icon={faYoutube} />Додати відео
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: leftTab === 'description'
                                ? 'repeat(auto-fill, minmax(170px, 1fr))'
                                : 'repeat(auto-fill, minmax(190px, 1fr))',
                            gap: 12,
                        }}>
                            {activeFiles.map(function(file) {
                                // YouTube картка
                                if (file.source === 'youtube') {
                                    return (
                                        <VideoCard
                                            key={file.id}
                                            file={file}
                                            albumId={albumId}
                                            onRemove={handleRemoveYoutube}
                                            showCaptionError={showCaptionError}
                                        />
                                    );
                                }
                                // Фото картка
                                return (
                                    <FileCard
                                        key={file.id}
                                        file={file}
                                        albumId={albumId}
                                        isCover={coverFileId === file.id}
                                        onTrash={handleTrash}
                                        onSetCover={handleSetCover}
                                        onView={handleView}
                                        showCaptionError={showCaptionError}
                                    />
                                );
                            })}
                            <div
                                onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                                style={{
                                    border: '1.5px dashed #ced4da', borderRadius: 10,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    justifyContent: 'center', minHeight: 160,
                                    cursor: 'pointer', color: '#adb5bd', background: '#fafafa', gap: 8,
                                }}
                                onMouseEnter={function(e) { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.borderColor = '#86b7fe'; }}
                                onMouseLeave={function(e) { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#ced4da'; }}
                            >
                                <FontAwesomeIcon icon={faUpload} style={{ fontSize: 22 }} />
                                <span style={{ fontSize: 13 }}>Додати фото</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {viewer && (
                <FileViewer
                    isOpen={!!viewer}
                    onClose={function() { setViewer(null); }}
                    file={viewer}
                    documentId={albumId}
                />
            )}
        </div>
    );
};

export default AlbumEditor;